import { BasketDeliveryTime } from '@/models/Basket';
import { Blog } from '@/models/BlogsResponse';
import { BreadcrumbModel } from '@/models/CategoryDetail';
import { City, District } from '@/models/City';
import { Product } from '@/models/Product';
import { type ClassValue, clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale/tr';
import { Metadata } from 'next';
import { twMerge } from 'tailwind-merge';
import { defaultMetaData } from './Constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumberToPriceText(number: number): string {
  try {
    let numberStr = number?.toString();

    let [integerPart, decimalPart] = numberStr.split('.');

    if (decimalPart) {
      decimalPart += '0';
      decimalPart = decimalPart.slice(0, 2);
    } else {
      decimalPart = '00';
    }

    return `${integerPart},${decimalPart}`;
  } catch (e) {
    return '0,00';
  }
}

export function calculateDiscountPercentage(price: number, priceBeforeDiscount: number): number {
  return Math.round(((priceBeforeDiscount - price) / priceBeforeDiscount) * 100);
}

export function normalizeText(text: string): string {
  return text
    ? text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replaceAll('&', '')
        .replaceAll('/', '-')
        .replaceAll('  ', ' ')
        .replaceAll(' ', '-')
        .replaceAll('ı', 'i')
        .replaceAll("'", '-')
        .replaceAll('’', '-')
        .replace(/-+/g, '-')
        .replace(/-$/g, '')
        .replace(/^-/g, '')
        .toLowerCase()
    : text;
}

export function normalizeUrl(text: string): string {
  return text
    ? text
        .split('/')
        .map((part) =>
          part
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replaceAll('  ', ' ')
            .replaceAll(' ', '-')
            .replaceAll('ı', 'i')
            .replaceAll("'", '-')
            .replaceAll('’', '-')
            .replaceAll('(', '-')
            .replaceAll(')', '-')
            .replaceAll(':', '-')
            .replace(/-+/g, '-')
            .replace(/-$/g, '')
            .replace(/^-/g, '')
            .toLowerCase(),
        )
        .join('/')
    : text;
}

export function normalizeSlug(slug: string): string {
  const normalizesSlug = normalizeUrl(decodeURIComponent(slug));
  if (normalizesSlug !== decodeURIComponent(slug)) {
    return normalizesSlug;
  }
  return slug;
}

export function addDays(date: Date, days: number): Date {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function formatDate(date: Date): string {
  var mm = date.getMonth() + 1;
  var dd = date.getDate();

  return [(dd > 9 ? '' : '0') + dd, (mm > 9 ? '' : '0') + mm, date.getFullYear()].join('.');
}

export function formatToISODate(date: Date): string {
  var mm = date.getMonth() + 1;
  var dd = date.getDate();

  return [date.getFullYear(), (mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd].join('-');
}

function toCalendarDate(value: string | Date): Date {
  if (value instanceof Date) {
    return value;
  }

  const iso = parseISO(value);
  if (!Number.isNaN(iso.getTime())) {
    return iso;
  }

  return new Date(value);
}

export function formatBasketDeliveryDate(deliveryTime: BasketDeliveryTime): string {
  try {
    if (deliveryTime.title === 'Kargo') {
      return 'Kargo';
    }

    const formattedDate = format(toCalendarDate(deliveryTime.date), 'd MMM yyyy', { locale: tr });

    return `${formattedDate} / ${deliveryTime.title}`;
  } catch (e) {
    return deliveryTime.title || '';
  }
}

export function formatOrderDeliveryDisplay(order: {
  deliveryTime?: BasketDeliveryTime | null;
  orderDate?: string;
}): string {
  if (order.deliveryTime) {
    const formatted = formatBasketDeliveryDate(order.deliveryTime);
    if (formatted) {
      return formatted;
    }
  }

  if (!order.orderDate) {
    return '';
  }

  const datePart = formatOrderDate(order.orderDate);
  const slotTitle = order.deliveryTime?.title;
  if (datePart && slotTitle && slotTitle !== 'Kargo') {
    return `${datePart} / ${slotTitle}`;
  }

  return datePart;
}

export function formatDeliveryDate(
  orderDate: string,
  orderStart: string,
  orderEnd: string,
): string {
  try {
    const startDateTime = new Date(`${orderDate}T${orderStart}`);
    const endDateTime = new Date(`${orderDate}T${orderEnd}`);

    const formattedDate = format(startDateTime, 'd MMM yyyy', { locale: tr });
    const formattedStart = format(startDateTime, 'HH:mm');
    const formattedEnd = format(endDateTime, 'HH:mm');

    return `${formattedDate} ${formattedStart} - ${formattedEnd}`;
  } catch (e) {
    return '';
  }
}

export function formatOrderDate(orderDate: string): string {
  try {
    const startDateTime = new Date(`${orderDate}`);

    const formattedDate = format(startDateTime, 'd MMM yyyy', { locale: tr });

    return formattedDate;
  } catch (e) {
    return '';
  }
}

export function isTrue(value?: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return ['true', 'yes', 'ok', '1'].includes(value.toLowerCase());
  }

  if (typeof value === 'number') {
    return value > 0;
  }

  return false;
}

export function isValidFullName(name?: string): boolean {
  if (!name?.trim()) {
    return false;
  }

  const trimmed = name.trim();
  return !/\d/.test(trimmed) && trimmed.split(/\s+/).length >= 2;
}

export const isDevelopment: boolean =
  process.env.NEXT_PUBLIC_WEBSITE_URL === 'http://localhost:3000' ||
  process.env.NEXT_PUBLIC_WEBSITE_URL === 'https://dev-test.hizlicicek.com';
// process.env.NEXT_PUBLIC_ENV === 'development' || process.env.NODE_ENV === 'development';

export function convertAndResizeUrl(
  url: string | undefined,
  width: number | undefined,
  height: number | undefined,
): string {
  if (!url) {
    return '';
  }
  const urlObj = new URL(url);

  return `${process.env.NEXT_PUBLIC_IMAGE_RESIZER_BASE_URL}${urlObj.pathname}?d=${width}x${height}`;
}

export function generateSrcSet(url: string) {
  return [300, 600, 900, 1200, 1600]
    .map((width) => `${convertAndResizeUrl(url, width, undefined)} ${width}w`)
    .join(', ');
}

export function getBlogJsonLd(blog: Blog) {
  if (!blog) {
    return {};
  }
  let cleanedHTML = blog.content?.replace(/\\"/g, '"').replace(/\\'/g, "'");
  const stripHTMLTags = cleanedHTML.replace(/<[^>]*>/g, '');

  const findImageInParsedContent = (str: string): string | undefined => {
    const urls: string[] = [];
    const rex = /<img[^>]+src=["']?([^"'>\s]+)["']?[^>]*>/g;
    let match;

    while ((match = rex.exec(str)) !== null) {
      urls.push(match[1]);
    }

    return urls?.[0];
  };

  const imageSrc = findImageInParsedContent(cleanedHTML);

  /**
   * EEAT-compliant Article schema.
   * - Author as Organization (matches AuthorBio component)
   * - datePublished / dateModified use real backend dates when available
   *
   * TODO [BACKEND]: Once publishedAt/updatedAt fields are added to Blog model,
   * the hardcoded fallback date below should be removed.
   */
  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blog.title,
    description: blog.description,
    author: {
      '@type': 'Person',
      name: 'Gülcan Kambak',
      jobTitle: 'Çiçek Tasarım Uzmanı',
      url: 'https://hizlicicek.com',
      image: 'https://hizlicicek.com/authors/gulcan-kambak.jpg',
      sameAs: [
        'https://www.instagram.com/hizlicicekcom/',
        'https://www.facebook.com/share/1C4iwcrKZn/',
      ],
      worksFor: {
        '@type': 'Organization',
        name: 'Hızlı Çiçek',
        url: 'https://hizlicicek.com',
      },
    },
    datePublished: blog.publishedAt || '2024-10-05',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hizlicicek.com/${blog.url}`,
    },
    publisher: {
      '@type': 'Organization',
      name: defaultMetaData.brand,
      url: defaultMetaData.url,
      logo: {
        '@type': 'ImageObject',
        url: defaultMetaData.logo,
      },
    },
    image: imageSrc,
    url: `https://hizlicicek.com/${blog.url}`,
    wordCount: stripHTMLTags.split(/\s+/).filter(Boolean).length,
    articleBody: stripHTMLTags,
  };

  // Only include dateModified if content was actually updated
  if (blog.updatedAt && blog.updatedAt !== blog.publishedAt) {
    jsonLd.dateModified = blog.updatedAt;
  }

  return jsonLd;
}

export function getProductJsonLd(product: Product) {
  const siteUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;

  const ratingValue = product.reviewData?.averageRating || '';
  const ratingCount = product.reviewData?.ratingCount || '';

  const result = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.title,
    image: product.imageUrl?.split('?')?.[0],
    description: product.seo?.description,
    brand: {
      '@type': 'Brand',
      name: 'Hızlıçiçek',
    },
    category: product.ldCategory || '',
    offers: {
      '@type': 'Offer',
      url: `${siteUrl}${product.url}`,
      priceCurrency: 'TRY',
      price: `${product.price}`,
      availability:
        product.inStock === 1 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  };

  if (ratingValue !== '' && ratingCount !== '') {
    result['aggregateRating'] = {
      '@type': 'AggregateRating',
      ratingValue: ratingValue,
      ratingCount: ratingCount,
    };
  }

  return result;
}

export function getBreadcrumbJsonLd(breadcrumb: BreadcrumbModel[]) {
  const siteUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;

  const breadcrumbItems = (breadcrumb ?? []).map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.title,
    item: `${siteUrl}${item.url}`,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };
}

export function getItemListJsonLd({
  name,
  url,
  products,
}: {
  name: string;
  url: string;
  products: Product[];
}) {
  const siteUrl = process.env.NEXT_PUBLIC_WEBSITE_URL || '';
  const safeUrl = url ?? '';
  const normalizedUrl = safeUrl.startsWith('http') ? safeUrl : `${siteUrl}${safeUrl}`;
  const safeProducts = products ?? [];

  return {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    name,
    url: normalizedUrl,
    numberOfItems: safeProducts.length,
    itemListElement: safeProducts.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: product?.title,
      url: (product?.url ?? '').startsWith('http')
        ? product.url
        : `${siteUrl}${product?.url ?? ''}`,
    })),
  };
}

export function safeMultiply(value: number, multiplier: number): number {
  return Math.round((value + Number.EPSILON) * multiplier);
}

export function localDate(date?: Date): string {
  if (!date) {
    return '';
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function isMobileDevice(): boolean {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isSmallScreen = window.innerWidth <= 768;
  return mobileRegex.test(ua.toLowerCase()) || isSmallScreen;
}

export function getCargoInfo(currentDate = new Date()) {
  const now = currentDate;

  // if (now.getFullYear() === 2025 && now.getMonth() === 5 && (now.getDate() >=5 && now.getDate() <= 5)) {
  //   return { cargoDay: 'Bayramdan Sonra', cargoDescription: 'Bayramdan sonra kargoda', cargoToday: false };
  // }

  const day = now.getDay(); // 0 = Pazar, 6 = Cumartesi
  const hour = now.getHours();

  let cutoff = new Date(now);

  const isSunday = day === 0;
  const isSaturday = day === 6;
  const isWeekday = day >= 1 && day <= 5;

  const saturdayLimitHour = 12;
  const weekdayLimitHour = 16;

  let cargoDay: 'Bugün' | 'Yarın' = 'Yarın';

  if (isSunday) {
    cargoDay = 'Yarın';
  } else if (isSaturday && hour < saturdayLimitHour) {
    cargoDay = 'Bugün';
    cutoff.setHours(saturdayLimitHour, 0, 0, 0);
  } else if (isWeekday && hour < weekdayLimitHour) {
    cargoDay = 'Bugün';
    cutoff.setHours(weekdayLimitHour, 0, 0, 0);
  } else {
    cargoDay = 'Yarın';
  }

  const cargoToday = cargoDay === 'Bugün';

  let cargoDescription: string;

  if (cargoDay === 'Bugün') {
    const diffMs = cutoff.getTime() - now.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    cargoDescription = `${hours > 0 ? `${hours} saat ` : ''}${minutes}dk içinde sipariş verirsen bugün kargoda!`;
  } else {
    cargoDescription = 'Yarın kargoda';
  }

  return { cargoDay, cargoDescription, cargoToday };
}

export function formatMetaData({
  meta,
  hideFromRobots,
  noIndexFollow,
}: {
  meta: Metadata;
  hideFromRobots?: boolean;
  noIndexFollow?: boolean;
}) {
  const robots =
    isDevelopment || hideFromRobots
      ? {
          index: false,
          follow: false,
          googleBot: {
            index: false,
            follow: false,
          },
        }
      : {
          index: !noIndexFollow,
          follow: true,
          googleBot: {
            index: !noIndexFollow,
            follow: true,
          },
        };
  if (!meta) {
    return { robots };
  }
  delete meta.alternates?.languages;
  delete meta.keywords;
  if (meta.robots) {
    if (isDevelopment) {
      meta.robots = {
        index: false,
        follow: false,
        googleBot: {
          index: false,
          follow: false,
        },
      };
    }
  } else {
    meta.robots = robots;
  }

  if (!meta?.openGraph) {
    meta.openGraph = {
      ...meta.openGraph,
      title: meta.title || defaultMetaData.title.default,
      description: meta.description || defaultMetaData.description,
      url: meta.alternates?.canonical?.toString() || defaultMetaData.url,
      siteName: 'Hızlı Çiçek',
      locale: defaultMetaData.locale,
      type: 'website',
    };
  }

  if (!meta?.twitter) {
    meta.twitter = {
      ...meta.twitter,
      title: meta.title || defaultMetaData.title.default,
      description: meta.description || defaultMetaData.description,
      images: meta.twitter?.images || [defaultMetaData.image],
      card: 'summary_large_image',
    };
  }

  if (meta.robots && meta.robots['index'] !== false) {
    meta.robots = {
      ...robots,
      'max-snippet': -1,
      'max-image-preview': 'large',
      'max-video-preview': -1,
    };
    if (meta.robots.googleBot) {
      meta.robots.googleBot['max-snippet'] = -1;
      meta.robots.googleBot['max-image-preview'] = 'large';
      meta.robots.googleBot['max-video-preview'] = -1;
    }
  }

  return meta;
}

export const districtToUrl = (district: District, cities: City[]): string | undefined => {
  const selectedCity = cities.find((city) => city.id === district.cityId);
  if (!selectedCity) {
    return;
  }
  const cityUrlPart = normalizeText(selectedCity.title).replaceAll(',', '').replace(/\r/g, '');
  const districtUrlPart = normalizeText(district.title).replaceAll(',', '').replace(/\r/g, '');
  if (cityUrlPart === 'diger-sehirler') {
    return `/bolge/${districtUrlPart}`;
  }
  return `/bolge/${cityUrlPart}-${districtUrlPart}`;
};

/** Native `webview` tam ekranı: `window.__hzcCloseWebView()` veya bridge `closeWebView`. `externalview` kapatılamaz. */
export function canCloseNativeWebView(): boolean {
  try {
    return !!(window as any).__hzcWebViewCloseable;
  } catch {
    return false;
  }
}

/** Native `webview` tam ekranını kapatır. Tab / `externalview` / tarayıcıda no-op. */
export function closeNativeWebView(): boolean {
  try {
    const fn = (window as any).__hzcCloseWebView;
    if (typeof fn === 'function') {
      return !!fn();
    }
  } catch {
    /* native inject yoksa bridge'e düş */
  }
  return (
    appRPC('bridge', 'closeWebView', undefined) ||
    appRPC('bridge', 'closeNativeFullscreen', undefined)
  );
}

export const appRPC = (type: 'bridge' | 'route', route: string, payload: any): boolean => {
  try {
    const iosBridge = (window as any)?.webkit?.messageHandlers?.app;
    if (iosBridge) {
      iosBridge.postMessage(
        payload && typeof payload === 'object'
          ? { action: route, ...payload }
          : { action: route, payload },
      );
      return true;
    }
  } catch (_) {
    return false;
  }

  let payloadAndroid: string | undefined = payload;
  if (payload && typeof payload === 'object') payloadAndroid = JSON.stringify(payload);

  try {
    if (type === 'route') {
      const androidRoute = (window as any)?.AndroidRoute;
      if (androidRoute && typeof androidRoute[route] === 'function') {
        if (payloadAndroid) androidRoute[route](payloadAndroid);
        else androidRoute[route]();
        return true;
      }
    } else {
      const bridge = (window as any)?.AppBridge;
      if (bridge && typeof bridge[route] === 'function') {
        if (payloadAndroid) bridge[route](payloadAndroid);
        else bridge[route]();
        return true;
      }
    }
  } catch (_) {
    return false;
  }
  return false;
};

export const checkRPC = (type: 'bridge' | 'route') => {
  try {
    const iosBridge = (window as any)?.webkit?.messageHandlers?.app;
    if (type === 'bridge') {
      const bridge = (window as any)?.AppBridge;
      if (bridge || iosBridge) {
        return true;
      }
    } else if (type === 'route') {
      const androidRoute = (window as any)?.AndroidRoute;
      if (androidRoute || iosBridge) {
        return true;
      }
    }
    return false;
  } catch (err) {
    return false;
  }
};
